<template>
  <!-- 거래상세 화면 (SPEC-CHINA-ECOUNT-SALES-LINES-001 + SPEC-CHINA-ECOUNT-TX-LINK-001) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountEtl_salesLines_title') }}</span>
      <a-tooltip :title="$t('ecountEtl_salesLines_collection')" placement="bottom">
        <QuestionCircleOutlined class="title-tip-icon" />
      </a-tooltip>
      <!-- SPEC-CHINA-ECOUNT-TX-LINK-001 파트 B: 연결키 설명 모달 버튼 -->
      <div style="margin-left: auto;">
        <a-button size="small" type="default" @click="showTxLinkModal = true">
          {{ $t('ecountTxLink_modal_title') }}
        </a-button>
      </div>
    </div>

    <!-- KPI 카드 6종 (REQ-FE-VIEW-002) -->
    <div class="kpi-row">
      <a-card
        v-for="kpi in kpiCards"
        :key="kpi.key"
        :loading="statsLoading"
        class="kpi-card"
        size="small"
      >
        <div class="kpi-label">{{ $t(kpi.labelKey) }}</div>
        <div class="kpi-value">{{ kpi.format ? kpi.format(statsData) : '-' }}</div>
      </a-card>
    </div>

    <!-- 검색 필터 카드 (REQ-FE-VIEW-003) -->
    <a-card style="margin-bottom: 16px;">
      <a-form layout="inline" class="sales-filter-form" :model="filters" @finish="onSearch">
        <!-- 파트 D: 거래처명 distinct 셀렉트 (customerName 기반, customerCode 없음) -->
        <a-form-item :label="$t('ecountEtl_salesLines_filter_customerName')">
          <a-select
            v-model:value="filters.customerName"
            :placeholder="$t('ecountEtl_salesLines_filter_customerName_distinct_ph')"
            :options="customerNameOptions"
            :loading="customersLoading"
            allow-clear
            show-search
            :filter-option="filterCustomerNameOption"
            style="min-width: 180px; max-width: 280px;"
          />
        </a-form-item>
        <!-- 품목코드 -->
        <a-form-item :label="$t('ecountEtl_salesLines_filter_productCode')">
          <a-input
            v-model:value="filters.ecountProductCode"
            :placeholder="$t('ecountEtl_salesLines_filter_productCode_ph')"
            allow-clear
            style="min-width: 140px;"
          />
        </a-form-item>
        <!-- 전표번호 -->
        <a-form-item :label="$t('ecountEtl_salesLines_filter_voucherNo')">
          <a-input
            v-model:value="filters.voucherNo"
            :placeholder="$t('ecountEtl_salesLines_filter_voucherNo_ph')"
            allow-clear
            style="width: 130px;"
          />
        </a-form-item>
        <!-- 거래일 범위 (KST range-picker) -->
        <a-form-item :label="$t('ecountEtl_salesLines_filter_dateRange')">
          <a-range-picker v-model:value="filters.dateRange" style="width: 240px;" />
        </a-form-item>
        <!-- 반품 여부 -->
        <a-form-item :label="$t('ecountEtl_salesLines_filter_isReturn')">
          <a-select
            v-model:value="filters.isReturn"
            allow-clear
            style="min-width: 110px;"
          >
            <a-select-option :value="true">{{ $t('ecountEtl_salesLines_filter_isReturn_yes') }}</a-select-option>
            <a-select-option :value="false">{{ $t('ecountEtl_salesLines_filter_isReturn_no') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item class="filter-actions">
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('ecountEtl_common_search') }}
            </a-button>
            <a-button @click="onReset">{{ $t('ecountEtl_common_reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 오류 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 툴바: 총건수 + pageSize -->
    <div class="toolbar-row">
      <span class="total-count">
        {{ $t('ecountEtl_common_total') }}: {{ formatNumber(pagination.total) }}
      </span>
      <div class="toolbar-controls">
        <a-select v-model:value="pageSize" size="small" style="width: 100px;" @change="onToolbarPageSizeChange">
          <a-select-option :value="10">10 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="20">20 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="50">50 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="100">100 / {{ $t('ecountEtl_common_page') }}</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 파트 A: 전 필드 테이블 — 16 core 컬럼 + expandedRowRender (데스크탑) -->
    <a-table
      v-if="!isMobileView"
      :data-source="salesLines"
      :columns="columns"
      :loading="loading"
      :pagination="false"
      :row-class-name="rowClassName"
      row-key="_id"
      size="small"
      :scroll="{ x: 2400 }"
      :expanded-row-keys="expandedRowKeys"
      @expand="onExpand"
    >
      <template #bodyCell="{ column, record }">
        <!-- 펼치기 버튼 -->
        <template v-if="column.key === 'expandBtn'">
          <a-button
            type="link"
            size="small"
            style="padding: 0; font-size: 10px;"
            @click="toggleExpand(record._id)"
          >{{ expandedRowKeys.includes(record._id) ? '▲' : '▼' }}</a-button>
        </template>
        <!-- 거래일 -->
        <template v-else-if="column.key === 'transactionDate'">
          {{ formatDate(record.transactionDate) }}
        </template>
        <!-- 수량 (음수=반품 시각 구분 REQ-FE-VIEW-005) -->
        <template v-else-if="column.key === 'qty'">
          <span :class="record.isReturn ? 'qty-return' : ''">{{ formatNumber(record.qty) }}</span>
        </template>
        <!-- 금액 컬럼 -->
        <template v-else-if="['unitPrice', 'unitPriceVatIncluded', 'supplyAmount', 'vatAmount', 'totalAmount'].includes(column.key)">
          {{ formatNumber(record[column.dataIndex]) }}
        </template>
        <!-- 반품 태그 (REQ-FE-VIEW-005) -->
        <template v-else-if="column.key === 'isReturn'">
          <a-tag v-if="record.isReturn" color="red">{{ $t('ecountEtl_salesLines_col_return') }}</a-tag>
          <span v-else>-</span>
        </template>
        <!-- serialArray lot 정보 (REQ-FE-VIEW-006: 빈 배열 가드) -->
        <template v-else-if="column.key === 'serialArray'">
          <template v-if="record.serialArray && record.serialArray.length > 0">
            <a-tooltip>
              <template #title>
                <div v-for="(s, i) in record.serialArray" :key="i" style="font-size: 11px; line-height: 1.6;">
                  <span v-if="s.lot">Lot: {{ s.lot }}</span>
                  <span v-if="s.gtin14"> | GTIN14: {{ s.gtin14 }}</span>
                  <span v-if="s.mfgDate"> | {{ s.mfgDate }}</span>
                </div>
              </template>
              <a-tag color="blue">{{ record.serialArray.length }} lot</a-tag>
            </a-tooltip>
          </template>
          <span v-else>-</span>
        </template>
        <!-- serialLotMatchKey -->
        <template v-else-if="column.key === 'serialLotMatchKey'">
          <span class="mono-key" :title="record.serialLotMatchKey">{{ record.serialLotMatchKey || '-' }}</span>
        </template>
        <!-- 파트 C: 매칭 Lot 수 drill-down 버튼 -->
        <template v-else-if="column.key === 'matchedSerialLotCount'">
          <a-button
            v-if="record.matchedSerialLotCount > 0"
            type="link"
            size="small"
            style="padding: 0;"
            @click="openDrilldown(record)"
          >{{ $t('ecountEtl_salesLines_drilldown_count', { count: record.matchedSerialLotCount }) }}</a-button>
          <span v-else style="color: #bfbfbf;">0</span>
        </template>
      </template>

      <!-- 파트 A: expandedRowRender — 9 hidden + 14 new fields 전 필드 표기 -->
      <template #expandedRowRender="{ record }">
        <div class="expanded-row-content">
          <!-- 그룹1: 식별자 정보 -->
          <div class="expand-group">
            <div class="expand-group-title">{{ $t('ecountEtl_salesLines_expand_group_identification') }}</div>
            <a-descriptions :column="3" size="small" bordered>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_dataSid')">{{ record.dataSid || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_hid')">{{ record.hid ?? '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_recordSid')">{{ record.recordSid || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_slipRecordSid')">{{ record.slipRecordSid || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_bizzSid')">{{ record.bizzSid || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_menuSid')">{{ record.menuSid || '-' }}</a-descriptions-item>
            </a-descriptions>
          </div>
          <!-- 그룹2: 거래 정보 -->
          <div class="expand-group">
            <div class="expand-group-title">{{ $t('ecountEtl_salesLines_expand_group_transaction') }}</div>
            <a-descriptions :column="3" size="small" bordered>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_voucherNo')">{{ record.voucherNo ?? '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_lineNo')">{{ record.lineNo ?? '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_summaryLine')">{{ record.summaryLine ?? '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_confirmType')">{{ record.confirmType || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_progressStatus')">{{ record.progressStatus || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_productSpecName')">{{ record.productSpecName || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_summaryNo')">{{ record.summaryNo || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_dataDtNo')">{{ record.dataDtNo || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_versionNo')">{{ record.versionNo ?? '-' }}</a-descriptions-item>
            </a-descriptions>
          </div>
          <!-- 그룹3: 금액 정보 -->
          <div class="expand-group">
            <div class="expand-group-title">{{ $t('ecountEtl_salesLines_expand_group_amount') }}</div>
            <a-descriptions :column="3" size="small" bordered>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_unitPrice')">{{ record.unitPrice != null ? formatNumber(record.unitPrice) : '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_unitPriceVat')">{{ record.unitPriceVatIncluded != null ? formatNumber(record.unitPriceVatIncluded) : '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_supplyAmount')">{{ record.supplyAmount != null ? formatNumber(record.supplyAmount) : '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_vatAmount')">{{ record.vatAmount != null ? formatNumber(record.vatAmount) : '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_totalAmount')">{{ record.totalAmount != null ? formatNumber(record.totalAmount) : '-' }}</a-descriptions-item>
            </a-descriptions>
          </div>
          <!-- 그룹4: 사용자 정의 텍스트 -->
          <div class="expand-group">
            <div class="expand-group-title">{{ $t('ecountEtl_salesLines_expand_group_customText') }}</div>
            <a-descriptions :column="3" size="small" bordered>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_txt001')">{{ record.txt001 || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_txt002')">{{ record.txt002 || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_txt003')">{{ record.txt003 || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_slipTxt003')">{{ record.slipTxt003 || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_slipTxt004')">{{ record.slipTxt004 || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_prodCont2')">{{ record.prodCont2 || '-' }}</a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_prodCont3')">{{ record.prodCont3 || '-' }}</a-descriptions-item>
            </a-descriptions>
          </div>
          <!-- 그룹5: 연결키 정보 -->
          <div class="expand-group">
            <div class="expand-group-title">{{ $t('ecountEtl_salesLines_expand_group_linkKey') }}</div>
            <a-descriptions :column="2" size="small" bordered>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_serialLotMatchKey')">
                <span v-if="record.serialLotMatchKey" class="match-key-cell">{{ record.serialLotMatchKey }}</span>
                <span v-else style="color: #bfbfbf;">-</span>
              </a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_trxConnSid')">{{ record.trxConnSid || '-' }}</a-descriptions-item>
            </a-descriptions>
          </div>
          <!-- 그룹6: Serial 정보 -->
          <div class="expand-group">
            <div class="expand-group-title">{{ $t('ecountEtl_salesLines_expand_group_serial') }}</div>
            <a-descriptions :column="2" size="small" bordered>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_serialArray')">
                <span v-if="!record.serialArray || record.serialArray.length === 0">{{ $t('ecountEtl_salesLines_serial_empty') }}</span>
                <span v-else>{{ record.serialArray.map(s => s.lot || s).join(', ') }}</span>
              </a-descriptions-item>
              <a-descriptions-item :label="$t('ecountEtl_salesLines_col_matchedSerialLotCount')">
                <a-button
                  v-if="record.matchedSerialLotCount > 0"
                  type="link"
                  size="small"
                  style="padding: 0;"
                  @click="openDrilldown(record)"
                >{{ $t('ecountEtl_salesLines_drilldown_count', { count: record.matchedSerialLotCount }) }}</a-button>
                <span v-else>0</span>
              </a-descriptions-item>
            </a-descriptions>
          </div>
        </div>
      </template>
    </a-table>

    <!-- 모바일: 카드뷰 -->
    <div v-else class="mobile-card-list">
      <a-spin v-if="loading" style="text-align: center; padding: 32px;" />
      <a-empty v-else-if="salesLines.length === 0" />
      <div v-else>
        <div v-for="record in salesLines" :key="record._id" class="mobile-company-card" :class="record.isReturn ? 'mobile-card-return' : ''">
          <!-- 핵심 정보 -->
          <div class="card-field">
            <span class="card-label">{{ $t('ecountEtl_salesLines_col_transactionDate') }}:</span>
            <span>{{ formatDate(record.transactionDate) }}</span>
            <a-tag v-if="record.isReturn" color="red" style="margin-left: auto;">{{ $t('ecountEtl_salesLines_col_return') }}</a-tag>
          </div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_salesLines_col_customerName') }}:</span><span>{{ record.customerName || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_salesLines_col_productName') }}:</span><span>{{ record.productName || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_salesLines_col_ecountProductCode') }}:</span><span>{{ record.ecountProductCode || '-' }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_salesLines_col_specName') }}:</span><span>{{ record.specName || '-' }}</span></div>
          <div class="card-field">
            <span class="card-label">{{ $t('ecountEtl_salesLines_col_qty') }}:</span>
            <span :class="record.isReturn ? 'qty-return' : ''">{{ formatNumber(record.qty) }}</span>
          </div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_salesLines_col_supplyAmount') }}:</span><span>{{ formatNumber(record.supplyAmount) }}</span></div>
          <div class="card-field"><span class="card-label">{{ $t('ecountEtl_salesLines_col_totalAmount') }}:</span><span>{{ formatNumber(record.totalAmount) }}</span></div>
          <!-- 연결키 -->
          <div v-if="record.serialLotMatchKey" class="card-field">
            <span class="card-label">{{ $t('ecountEtl_salesLines_col_serialLotMatchKey') }}:</span>
            <span class="mono-key">{{ record.serialLotMatchKey }}</span>
          </div>
          <!-- 거래상세 drill-down -->
          <div v-if="record.matchedSerialLotCount > 0" class="card-field">
            <span class="card-label">{{ $t('ecountEtl_salesLines_col_matchedSerialLotCount') }}:</span>
            <a-button type="link" size="small" style="padding: 0;" @click="openDrilldown(record)">
              {{ $t('ecountEtl_salesLines_drilldown_count', { count: record.matchedSerialLotCount }) }}
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <div class="pagination-row">
      <a-pagination
        v-model:current="pagination.current"
        :total="pagination.total"
        :page-size="pageSize"
        :show-total="totalLabel"
        show-size-changer
        :page-size-options="['10', '20', '50', '100']"
        @change="onPageChange"
        @show-size-change="onPageSizeChange"
      />
    </div>

    <!-- 파트 B: 연결키 설명 모달 -->
    <ecount-tx-link-info-modal
      :visible="showTxLinkModal"
      @close="showTxLinkModal = false"
    />

    <!-- 파트 C: drill-down 모달 (SalesLine → N serial_lot_transactions) -->
    <a-modal
      :open="drilldownVisible"
      :title="drilldownTitle"
      :footer="null"
      :width="900"
      @cancel="drilldownVisible = false"
    >
      <div v-if="drilldownLoading" style="text-align: center; padding: 24px;">
        <a-spin />
      </div>
      <a-alert v-else-if="drilldownError" type="error" :message="drilldownError" show-icon />
      <div v-else>
        <a-empty v-if="drilldownData.length === 0" :description="$t('ecountEtl_salesLines_drilldown_empty')" />
        <a-table
          v-else
          :data-source="drilldownData"
          :columns="drilldownColumns"
          :pagination="drilldownPagination.total > drilldownPagination.pageSize ? drilldownPagination : false"
          row-key="_id"
          size="small"
          :scroll="{ x: 800 }"
          @change="onDrilldownTableChange"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'transactionDate'">
              {{ formatDate(record.transactionDate) }}
            </template>
            <template v-else-if="column.key === 'transactionDirection'">
              <a-tag :color="record.transactionDirection === 'INBOUND' ? 'green' : 'blue'">{{ record.transactionDirection || '-' }}</a-tag>
            </template>
            <template v-else-if="column.key === 'qty'">
              {{ record.qty != null ? formatNumber(record.qty) : '-' }}
            </template>
          </template>
        </a-table>
      </div>
    </a-modal>
  </div>
</template>

<script>
// @MX:ANCHOR: [AUTO] SalesLines 화면 진입점 — ecountEtl API 4개 소비, 파트A/B/C/D 통합
// @MX:REASON: [AUTO] SPEC-CHINA-ECOUNT-TX-LINK-001 파트 A/B/C/D 중심 뷰. fan_in >= 4 API 호출
// @MX:SPEC: SPEC-CHINA-ECOUNT-SALES-LINES-001, SPEC-CHINA-ECOUNT-TX-LINK-001

import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import {
  getSalesLines,
  getSalesLinesStats,
  getSalesLinesCustomersDistinct,
  getSalesLineSerialLots,
  extractApiError,
  parsePaginatedResponse,
} from '../../apis/ecountEtl.js';
import dayjs from 'dayjs';
import EcountTxLinkInfoModal from '../../components/DealerGsp/EcountTxLinkInfoModal.vue';

// 300ms debounce 유틸
function debounce(fn, delay) {
  let timer;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

export default {
  name: 'EcountEtlSalesLines',
  components: { QuestionCircleOutlined, EcountTxLinkInfoModal },

  data() {
    return {
      isMobileView: typeof window !== 'undefined' && window.innerWidth <= 767,
      loading: false,
      error: null,
      salesLines: [],
      pageSize: 20,
      pagination: { current: 1, total: 0 },
      // 필터 상태 (REQ-FE-VIEW-003)
      filters: {
        customerName: undefined,
        ecountProductCode: '',
        voucherNo: '',
        dateRange: null,
        isReturn: undefined,
      },
      // KPI 통계 상태 (REQ-FE-VIEW-002)
      statsData: null,
      statsLoading: false,
      // 파트 D: 거래처명 distinct 셀렉트
      customerNameOptions: [],
      customersLoading: false,
      // 파트 B: 연결키 모달
      showTxLinkModal: false,
      // 파트 A: expandedRowKeys
      expandedRowKeys: [],
      // 파트 C: drill-down 모달
      drilldownVisible: false,
      drilldownLoading: false,
      drilldownError: null,
      drilldownTitle: '',
      drilldownData: [],
      drilldownCurrentRecordId: null,
      drilldownPagination: { current: 1, total: 0, pageSize: 20 },
    };
  },

  computed: {
    // KPI 카드 6종 정의 (REQ-FE-VIEW-002)
    kpiCards() {
      return [
        {
          key: 'totalLines',
          labelKey: 'ecountEtl_salesLines_kpi_totalLines',
          format: (d) => d ? this.formatNumber(d.totalLines) : '-',
        },
        {
          key: 'totalQty',
          labelKey: 'ecountEtl_salesLines_kpi_totalQty',
          format: (d) => d ? this.formatNumber(d.totalQty) : '-',
        },
        {
          key: 'totalSupplyAmount',
          labelKey: 'ecountEtl_salesLines_kpi_totalSupplyAmount',
          format: (d) => d ? this.formatNumber(d.totalSupplyAmount) : '-',
        },
        {
          key: 'totalAmount',
          labelKey: 'ecountEtl_salesLines_kpi_totalAmount',
          format: (d) => d ? this.formatNumber(d.totalAmount) : '-',
        },
        {
          key: 'returnCount',
          labelKey: 'ecountEtl_salesLines_kpi_returnCount',
          format: (d) => d ? this.formatNumber(d.returnCount) : '-',
        },
        {
          key: 'customerCount',
          labelKey: 'ecountEtl_salesLines_kpi_customerCount',
          format: (d) => d ? this.formatNumber(d.customerCount) : '-',
        },
      ];
    },

    // 테이블 컬럼 정의 — 16 core + expandBtn + matchedSerialLotCount (파트 A/C)
    columns() {
      return [
        { key: 'expandBtn', title: '', width: 36, align: 'center', fixed: 'left' },
        { key: 'transactionDate', dataIndex: 'transactionDate', title: this.$t('ecountEtl_salesLines_col_transactionDate'), width: 110 },
        { key: 'customerName', dataIndex: 'customerName', title: this.$t('ecountEtl_salesLines_col_customerName'), width: 160, ellipsis: true },
        { key: 'ecountProductCode', dataIndex: 'ecountProductCode', title: this.$t('ecountEtl_salesLines_col_ecountProductCode'), width: 130 },
        { key: 'productName', dataIndex: 'productName', title: this.$t('ecountEtl_salesLines_col_productName'), width: 180, ellipsis: true },
        { key: 'specName', dataIndex: 'specName', title: this.$t('ecountEtl_salesLines_col_specName'), width: 130, ellipsis: true },
        { key: 'qty', dataIndex: 'qty', title: this.$t('ecountEtl_salesLines_col_qty'), width: 70, align: 'right' },
        { key: 'unitPrice', dataIndex: 'unitPrice', title: this.$t('ecountEtl_salesLines_col_unitPrice'), width: 100, align: 'right' },
        { key: 'unitPriceVatIncluded', dataIndex: 'unitPriceVatIncluded', title: this.$t('ecountEtl_salesLines_col_unitPriceVat'), width: 110, align: 'right' },
        { key: 'supplyAmount', dataIndex: 'supplyAmount', title: this.$t('ecountEtl_salesLines_col_supplyAmount'), width: 110, align: 'right' },
        { key: 'vatAmount', dataIndex: 'vatAmount', title: this.$t('ecountEtl_salesLines_col_vatAmount'), width: 90, align: 'right' },
        { key: 'totalAmount', dataIndex: 'totalAmount', title: this.$t('ecountEtl_salesLines_col_totalAmount'), width: 110, align: 'right' },
        { key: 'isReturn', dataIndex: 'isReturn', title: this.$t('ecountEtl_salesLines_col_isReturn'), width: 70, align: 'center' },
        { key: 'remark', dataIndex: 'remark', title: this.$t('ecountEtl_salesLines_col_remark'), width: 160, ellipsis: true },
        { key: 'employeeName', dataIndex: 'employeeName', title: this.$t('ecountEtl_salesLines_col_employeeName'), width: 100 },
        { key: 'serialArray', dataIndex: 'serialArray', title: this.$t('ecountEtl_salesLines_col_serialArray'), width: 90, align: 'center' },
        { key: 'serialLotMatchKey', dataIndex: 'serialLotMatchKey', title: this.$t('ecountEtl_salesLines_col_serialLotMatchKey'), width: 200, ellipsis: true },
        // 파트 C: 매칭 Lot 수 drill-down
        { key: 'matchedSerialLotCount', dataIndex: 'matchedSerialLotCount', title: this.$t('ecountEtl_salesLines_col_matchedSerialLotCount'), width: 90, align: 'center' },
      ];
    },

    // 파트 C: drill-down 모달 컬럼
    drilldownColumns() {
      return [
        { key: 'transactionDate', dataIndex: 'transactionDate', title: this.$t('ecountEtl_transactions_col_transactionDate'), width: 110 },
        { key: 'lotNumber', dataIndex: 'lotNumber', title: this.$t('ecountEtl_transactions_col_lotNumber'), width: 130 },
        { key: 'transactionDirection', dataIndex: 'transactionDirection', title: this.$t('ecountEtl_transactions_col_transactionDirection'), width: 90, align: 'center' },
        { key: 'transactionTypeEnum', dataIndex: 'transactionTypeEnum', title: this.$t('ecountEtl_transactions_col_transactionTypeEnum'), width: 160, ellipsis: true },
        { key: 'qty', dataIndex: 'qty', title: this.$t('ecountEtl_transactions_col_qty'), width: 70, align: 'right' },
        { key: 'customerName', dataIndex: 'customerName', title: this.$t('ecountEtl_transactions_col_customerName'), width: 150, ellipsis: true },
      ];
    },
  },

  mounted() {
    // 파트 D: distinct 셀렉트 데이터 + 딥링크 쿼리 복원 후 목록/통계 병렬 호출
    this.fetchCustomers();
    this.applyQueryToFilters();
    Promise.all([this.fetchData(), this.fetchStats()]);
    this._debouncedRefresh = debounce(() => {
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    }, 300);
    window.addEventListener('resize', this.onResize);
  },

  beforeUnmount() {
    if (this._debouncedRefresh) this._debouncedRefresh.cancel();
    window.removeEventListener('resize', this.onResize);
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        if (this._debouncedRefresh) this._debouncedRefresh();
      },
    },
  },

  methods: {
    onResize() { this.isMobileView = window.innerWidth <= 767; },

    // 파트 D: 거래처명 distinct 셀렉트 데이터 로드
    async fetchCustomers() {
      this.customersLoading = true;
      try {
        const res = await getSalesLinesCustomersDistinct();
        const data = res?.data || [];
        // @MX:NOTE: distinct 응답은 거래처명 문자열 배열(string[]) — BE getSalesLinesCustomers Promise<string[]>.
        //           과거 c.customerName 로 객체 속성을 읽어 옵션 value/label 이 전부 undefined → 빈 드롭다운 + 필터 미적용 버그.
        //           문자열을 직접 매핑한다. 객체 형태도 방어적으로 허용.
        this.customerNameOptions = data
          .map((c) => (typeof c === 'string' ? c : c?.customerName))
          .filter(Boolean)
          .map((name) => ({ value: name, label: name }));
      } catch (_) {
        // 실패 시 빈 옵션 유지
      } finally {
        this.customersLoading = false;
      }
    },

    // 거래처명 옵션 검색
    filterCustomerNameOption(input, option) {
      return (option.label || '').toLowerCase().includes((input || '').toLowerCase());
    },

    // 파트 C 딥링크: TransactionsList / LotFlowDashboard → SalesLines query 복원
    applyQueryToFilters() {
      const q = this.$route?.query || {};
      if (q.voucherNo) this.filters.voucherNo = String(q.voucherNo);
      if (q.customerName) this.filters.customerName = String(q.customerName);
      if (q.ecountProductCode) this.filters.ecountProductCode = String(q.ecountProductCode);
      // 유통흐름 딥링크: dateFrom/dateTo(YYYY-MM-DD) → dateRange 복원.
      // voucherNo는 날짜마다 중복되므로 거래일 범위와 함께 적용해야 해당 슬립으로 좁혀짐.
      if (q.dateFrom) {
        const from = dayjs(String(q.dateFrom));
        const to = q.dateTo ? dayjs(String(q.dateTo)) : from;
        if (from.isValid() && to.isValid()) {
          this.filters.dateRange = [from, to];
        }
      }
    },

    onSearch() {
      if (this._debouncedRefresh) this._debouncedRefresh.cancel();
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    },

    onReset() {
      if (this._debouncedRefresh) this._debouncedRefresh.cancel();
      this.filters = {
        customerName: undefined,
        ecountProductCode: '',
        voucherNo: '',
        dateRange: null,
        isReturn: undefined,
      };
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    },

    onPageChange(page) {
      this.pagination.current = page;
      this.fetchData();
    },

    onPageSizeChange(current, size) {
      this.pageSize = size;
      this.pagination.current = 1;
      this.fetchData();
    },

    onToolbarPageSizeChange(size) {
      this.pageSize = size;
      this.pagination.current = 1;
      this.fetchData();
    },

    // @MX:NOTE: [AUTO] KST range-picker 값을 UTC YYYYMMDD로 변환 (REQ-FE-VIEW-007, D-4)
    // 과거 date-only vs datetime 불일치로 400 발생 이력 반영 — 8자리 숫자 형식으로 전달
    toYYYYMMDD(d) {
      if (!d) return undefined;
      // dayjs 객체 (ant-design-vue range-picker 반환값)
      if (typeof d.year === 'function' && typeof d.month === 'function' && typeof d.date === 'function') {
        return `${d.year()}${String(d.month() + 1).padStart(2, '0')}${String(d.date()).padStart(2, '0')}`;
      }
      return undefined;
    },

    // 필터 파라미터 빌드 — BE param 6필터 전수 전달 (REQ-FE-API-001 [HARD])
    buildFilterParams() {
      const params = {};
      // 거래처명 (customerName 기반, customerCode 없음 D-1)
      if (this.filters.customerName) params.customerName = this.filters.customerName;
      if (this.filters.ecountProductCode) params.ecountProductCode = this.filters.ecountProductCode;
      // 전표번호 (숫자 변환)
      if (this.filters.voucherNo) {
        const n = Number(this.filters.voucherNo);
        if (!Number.isNaN(n)) params.voucherNo = n;
      }
      // 날짜 범위 — KST → UTC YYYYMMDD 변환 (빈 값 미전송)
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        const from = this.toYYYYMMDD(this.filters.dateRange[0]);
        const to = this.toYYYYMMDD(this.filters.dateRange[1]);
        if (from) params.dateFrom = from;
        if (to) params.dateTo = to;
      }
      // 반품 여부 (boolean, undefined 시 미전송)
      if (this.filters.isReturn !== undefined && this.filters.isReturn !== null) {
        params.isReturn = this.filters.isReturn;
      }
      return params;
    },

    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pageSize,
          sortBy: 'transactionDate',
          sortOrder: 'desc',
          ...this.buildFilterParams(),
        };
        const res = await getSalesLines(params);
        const parsed = parsePaginatedResponse(res);
        this.salesLines = parsed.data || [];
        this.pagination.total = parsed.total ?? 0;
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
        this.salesLines = [];
        this.pagination.total = 0;
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      this.statsLoading = true;
      try {
        const params = this.buildFilterParams();
        const res = await getSalesLinesStats(params);
        this.statsData = res?.data || null;
      } catch (_) {
        // 통계 실패 시 KPI 카드 '-' 유지 (목록 표시 방해하지 않음)
        this.statsData = null;
      } finally {
        this.statsLoading = false;
      }
    },

    // 파트 A: expand 토글
    onExpand(expanded, record) {
      const id = record._id;
      if (expanded) {
        if (!this.expandedRowKeys.includes(id)) this.expandedRowKeys = [...this.expandedRowKeys, id];
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((k) => k !== id);
      }
    },

    toggleExpand(id) {
      if (this.expandedRowKeys.includes(id)) {
        this.expandedRowKeys = this.expandedRowKeys.filter((k) => k !== id);
      } else {
        this.expandedRowKeys = [...this.expandedRowKeys, id];
      }
    },

    // 파트 C: drill-down 모달 열기
    async openDrilldown(record) {
      this.drilldownCurrentRecordId = record._id;
      this.drilldownTitle = this.$t('ecountEtl_salesLines_drilldown_title')
        + ' — ' + (record.voucherNo ?? '-') + '-' + (record.lineNo ?? '-');
      this.drilldownPagination = { current: 1, total: record.matchedSerialLotCount || 0, pageSize: 20 };
      this.drilldownVisible = true;
      this.drilldownData = [];
      this.drilldownError = null;
      await this.fetchDrilldown(record._id, 1);
    },

    async fetchDrilldown(recordId, page) {
      this.drilldownLoading = true;
      this.drilldownError = null;
      try {
        const res = await getSalesLineSerialLots(recordId, { page, limit: this.drilldownPagination.pageSize });
        const parsed = parsePaginatedResponse(res);
        this.drilldownData = parsed.data || [];
        this.drilldownPagination = { ...this.drilldownPagination, current: page, total: parsed.total ?? 0 };
      } catch (e) {
        const err = extractApiError(e);
        this.drilldownError = err.message;
        this.drilldownData = [];
      } finally {
        this.drilldownLoading = false;
      }
    },

    onDrilldownTableChange(pagination) {
      if (this.drilldownCurrentRecordId) {
        this.fetchDrilldown(this.drilldownCurrentRecordId, pagination.current);
      }
    },

    // 반품 행 스타일 (REQ-FE-VIEW-005)
    rowClassName(record) {
      return record.isReturn ? 'sales-line-return-row' : '';
    },

    // 숫자 포맷 (천 단위 구분)
    formatNumber(val) {
      if (val === undefined || val === null) return '-';
      return Number(val).toLocaleString();
    },

    // 날짜 포맷 (YYYYMMDD → YYYY-MM-DD)
    formatDate(val) {
      if (!val) return '-';
      const s = String(val);
      if (s.length === 8) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
      // ISO 날짜 문자열 처리
      const d = new Date(val);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      return s;
    },

    // 페이지네이션 총건수 라벨
    totalLabel(total) {
      return `${this.$t('ecountEtl_common_total')}: ${total.toLocaleString()}`;
    },
  },
};
</script>

<style scoped>
.page-header { display: flex; align-items: center; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 600; }
.title-tip-icon { color: rgba(0, 0, 0, 0.45); cursor: help; font-size: 16px; margin-left: 6px; }

/* KPI 카드 */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 767px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
}
.kpi-card { text-align: center; }
.kpi-label { font-size: 12px; color: #888; margin-bottom: 4px; }
.kpi-value { font-size: 20px; font-weight: 600; color: #333; }

/* 필터 폼 */
.sales-filter-form :deep(.ant-form-item) { margin-bottom: 12px; }
.sales-filter-form :deep(.ant-form-item:last-child) { margin-bottom: 0; }
.filter-actions { margin-bottom: 0; margin-left: auto; margin-right: 0; }

/* 툴바 */
.toolbar-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; padding: 0 4px;
}
.total-count { font-size: 13px; color: #666; }
.toolbar-controls { display: flex; gap: 8px; }

/* 페이지네이션 */
.pagination-row { display: flex; justify-content: flex-end; margin-top: 16px; }

/* 반품 행 강조 (REQ-FE-VIEW-005) */
:deep(.sales-line-return-row) { background-color: #fff1f0; }
:deep(.sales-line-return-row:hover > td) { background-color: #ffe0de !important; }
.qty-return { color: #cf1322; font-weight: 600; }

/* serialLotMatchKey 모노스페이스 */
.mono-key { font-family: monospace; font-size: 11px; color: #555; }

/* 파트 A: 전 필드 확장 영역 */
.expanded-row-content {
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.expand-group {
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.expand-group-title {
  background: #fafafa;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #595959;
  border-bottom: 1px solid #f0f0f0;
}

/* 파트 C: matchKey 셀 */
.match-key-cell {
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  color: #722ed1;
  background: #f9f0ff;
  border-radius: 3px;
  padding: 1px 4px;
}

:deep(.ant-descriptions-item-label) { font-size: 12px; color: #8c8c8c; }
:deep(.ant-descriptions-item-content) { font-size: 12px; }

/* 모바일 카드뷰 */
.mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
.mobile-company-card {
  border: 1px solid #f0f0f0; border-radius: 6px; padding: 12px; background: #fff;
}
.mobile-card-return { background-color: #fff1f0; border-color: #ffccc7; }
.card-field { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; }
.card-label { font-weight: 600; min-width: 90px; color: #666; }
.card-actions {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;
  margin-top: 10px; border-top: 1px solid #f5f5f5; padding-top: 8px;
}
.card-actions > :first-child { grid-column: 1 / -1; }
.card-actions :deep(.ant-btn) { width: 100%; padding-left: 4px; padding-right: 4px; }

@media (max-width: 767px) {
  .page-header { flex-wrap: wrap; gap: 8px; row-gap: 8px; }
  .toolbar-row { flex-direction: column; align-items: stretch; gap: 8px; }
  .toolbar-controls { justify-content: flex-end; }
  .pagination-row { justify-content: center; }
}
</style>
